import { Container, Stack } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { Popup } from 'components/common/popup/Popup';
import { IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { FormLayoutContext, IFormLayoutContext } from 'components/common/form/layout/tab';
import FormTabsContent from 'components/common/form/layout/tab/FormTabsContent';
import { adjustDefaultPopupSettingsByType, getFormId } from 'components/common/form/formTools';
import useBundleTranslation from 'i18n';
import StaticInfo from 'components/common/StaticInfo';

interface IFormPopupLayoutProps extends Omit<
    IFormLayoutProps,
    'confirmPopupProps' | 'subFormPopup' | 'panelSettings' | 'sharedComponents'
> {}

export default function FormPopupLayout({
    asPopupProps,
    elementProps,
    tabs,
    componentsMap,
    hookForm,
    customTabs,
    uid,
    isNew,
    entityType,
}: IFormPopupLayoutProps) {
    const { t } = useBundleTranslation(elementProps.translationNS);
    const submit = useRef(
        hookForm.form.hookFormHandleSubmit(function (data) {
            return hookForm.form.formAct.act.submit.exec(data);
        }),
    );

    // Exception for grid form
    if (uid == 'grid_popup_categoryGrid') {
        uid = 'category';
    }

    const id = getFormId(uid, isNew);
    const layoutContextValue = useMemo<IFormLayoutContext>(
        () => ({ formLayout: 'popup', formType: asPopupProps.popupType }),
        [asPopupProps.popupType],
    );

    const formRef = useRef<HTMLFormElement>(null);
    useEffect(() => {
        if (!formRef.current) {
            return;
        }
        const focusableElement: HTMLElement | null = formRef.current.querySelector('[data-focusable]');
        if (focusableElement) {
            focusableElement.querySelector('input')?.focus();
        }
    }, [formRef.current]);

    const isDisabledPopupConfirmButton = hookForm.form.hookFormWatch('disable_popup_confirm_button');

    const onConfirm = () => {
        hookForm.form.formAct.act.submit.exec(hookForm.form.hookFormGetValues());
    };

    return (
        <Popup
            settings={{
                ...adjustDefaultPopupSettingsByType(asPopupProps.popupSettings, asPopupProps.popupType, t, entityType),
                uid: 'p_' + uid,
            }}
            onHide={asPopupProps.onHide}
            onConfirm={onConfirm}
            open={asPopupProps.isOpen}
            translationNS={elementProps.translationNS}
            okDisabled={isDisabledPopupConfirmButton === 'Y'}
        >
            {hookForm.form.hookFormState.errors.__general &&
                componentsMap.findIndex((c) => c.componentName == 'ImportErrorStack') == -1 && (
                    <Container
                        sx={{ pb: 2 }}
                        className={'mi-container-3'}
                        maxWidth={false}
                        component="main"
                        disableGutters={true}
                    >
                        <StaticInfo
                            label={hookForm.form.hookFormState.errors.__general?.message as string}
                            align={'center'}
                            type={'error'}
                        />
                    </Container>
                )}
            <form ref={formRef} id={id} onSubmit={submit.current} className={'form-popup'}>
                <Stack className={'form-popup__content'}>
                    <FormLayoutContext.Provider value={layoutContextValue}>
                        <FormTabsContent
                            componentsMap={componentsMap}
                            formTabs={tabs.formTabs}
                            elementProps={elementProps}
                            customTabs={customTabs}
                        />
                    </FormLayoutContext.Provider>
                </Stack>
            </form>
        </Popup>
    );
}
